import type { AdData, AdType } from "@adland/data";
import { parseAdTimestamp } from "./time";

/**
 * Which image represents each ad type, most representative first.
 *
 * Per type, and ordered for the shape of the slot this fills. An ad card is
 * wide — the component renders it at 3:2 — so the banner-shaped asset wins:
 * a link's Open Graph card, a mini app's `imageUrl`. Their square marks (`icon`)
 * are the fallback, not the preference, because a 2000x2000 app icon letterboxed
 * into a 3:2 frame wastes most of it.
 *
 * This was one flat list for every type, which put `icon` ahead of `imageUrl`
 * and showed mini apps as their launcher icon where their embed art belonged.
 * A caller rendering small squares wants the opposite order and should pick
 * from the fields directly rather than reuse this.
 */
const IMAGE_KEYS: Record<string, readonly string[]> = {
  link: ["image", "icon"],
  miniapp: ["imageUrl", "icon"],
  token: ["logoURI"],
  farcasterProfile: ["pfpUrl"],
  // A post's picture is whatever was attached to it; the author's avatar is
  // only a stand-in for a caller that must render something wide. Anything
  // laying a post out properly should use `getAdPost`, where the two are
  // separate fields and the fallback cannot be mistaken for the post's media.
  cast: ["image", "pfpUrl"],
  tweet: ["image", "pfpUrl"],
};

/**
 * The square mark for each type, most representative first — the mirror of
 * `IMAGE_KEYS`.
 *
 * The two exist because an ad has both, and which one is right depends on the
 * shape of the space it goes in rather than on the ad. A mini app publishes a
 * 2000x2000 `iconUrl` and an 840x560 `imageUrl`; a wide card wants the second
 * and a compact row wants the first, and neither is more correct in general.
 */
const ICON_KEYS: Record<string, readonly string[]> = {
  link: ["icon", "image"],
  miniapp: ["icon", "imageUrl"],
  token: ["logoURI"],
  farcasterProfile: ["pfpUrl"],
  cast: ["pfpUrl"],
  tweet: ["pfpUrl"],
};

/** For an ad type this build has never heard of — every image key we know. */
const ANY_IMAGE_KEYS = [
  "image",
  "imageUrl",
  "icon",
  "pfpUrl",
  "logoURI",
] as const;

const ANY_ICON_KEYS = [
  "icon",
  "pfpUrl",
  "logoURI",
  "imageUrl",
  "image",
] as const;
const TITLE_KEYS = [
  "title",
  "displayName",
  "username",
  "name",
  "symbol",
] as const;
const DESC_KEYS = ["description", "bio", "text", "name"] as const;

function flatFields(data: AdData): Record<string, unknown> {
  return { ...data.data, ...(data.metadata ?? {}) };
}

/**
 * The ad's OWN picture — never a stand-in.
 *
 * `IMAGE_KEYS` deliberately falls through to a square mark or an avatar so a
 * caller that must render something wide always has a URL. That fallback is
 * right for a thumbnail and wrong for a banner: a cast with no attachment
 * became its author's avatar stretched across the space, and a profile became
 * a bare face with none of the bio that makes it an ad.
 *
 * This reports only the real thing, so a caller can tell "there is no picture"
 * from "here is a substitute" and choose what to do about it — which is what
 * `AdImage` uses to reach for a rendered card instead.
 */
const MEDIA_KEYS: Record<string, readonly string[]> = {
  // Both still report their artwork, and `getAdMedia` is what the card
  // renderer reads to decide whether to draw it edge to edge. What changed is
  // that `AdImage` no longer stops here — see `getAdCardKey`, which now
  // answers for every type, so even a finished picture arrives inside the
  // frame carrying its own label.
  link: ["image"],
  miniapp: ["imageUrl"],
  // Empty, now that a token has a card. The logo is a round mark a hundred
  // pixels across, and treating it as the ad's media stretched it over a
  // banner and skipped the rendered card entirely — price, market cap, volume
  // and liquidity, all resolved and never drawn. The logo is still the last
  // resort below, and it is still what `AdIcon` draws.
  token: [],
  // Empty on purpose. An avatar is square and the slot is wide, and the bio is
  // the part worth reading.
  farcasterProfile: [],
  // Empty, like a token and a profile. A post is words AND an author AND
  // whatever was attached to it; handing back only the attachment threw away
  // two of the three and rendered a photograph with no indication of who
  // posted it or what they said. The card composes all three.
  cast: [],
  tweet: [],
};

/**
 * How an ad's subject is addressed on the card renderer, or null.
 *
 * A cast, a post and a profile carry their own canonical id, so a card for them
 * can be drawn without knowing where the ad's metadata was stored. That matters
 * for every `<Ad data={…}>` with no chain fetch behind it — a static embed has
 * no CID at all, and used to fall all the way back to the author's avatar.
 *
 * Null for a link or a mini app: what those advertise is whatever the
 * advertiser stored, which only the CID names.
 */
export function getAdCardKey(data: AdData | null): string | null {
  if (!data) return null;
  const fields = flatFields(data);
  const str = (key: string) => {
    const v = fields[key];
    return typeof v === "string" && v ? v : null;
  };

  if (data.type === "cast") {
    const hash = str("hash");
    return hash && /^0x[0-9a-fA-F]{8,64}$/.test(hash) ? `cast/${hash}` : null;
  }

  if (data.type === "tweet") {
    // The id, not the URL: the renderer rebuilds the URL itself so the only
    // host it will ever fetch a post from is x.com.
    const id = str("url")?.match(/status\/(\d{1,32})/)?.[1];
    return id ? `tweet/${id}` : null;
  }

  if (data.type === "farcasterProfile") {
    const fid = str("fid");
    return fid && /^\d{1,32}$/.test(fid) ? `fid/${fid}` : null;
  }

  // A link and a mini app are addressed by the URL they point at. It is a
  // canonical id like the others — the same site advertised twice is one
  // picture — and unlike the CID it exists for a static `<Ad data>` that never
  // fetched from chain.
  //
  // Host and path only: the scheme is always https on the renderer's side, and
  // a query string is dropped because it is where tracking parameters live and
  // two ads pointing at the same page should not be two cache entries.
  if (data.type === "link" || data.type === "miniapp") {
    const raw = str("url");
    if (!raw) return null;
    try {
      const u = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
      if (!/^https?:$/.test(u.protocol)) return null;
      const path = u.pathname === "/" ? "" : u.pathname;
      return `site/${u.host}${path}`;
    } catch {
      return null;
    }
  }

  if (data.type === "token") {
    // Chain first, because the same address is a different token on a
    // different chain — the pair is the identity, not the address alone.
    const address = str("address");
    const chainId = fields.chainId;
    const chain =
      typeof chainId === "number" ? String(chainId) : String(chainId ?? "");
    return address && /^0x[0-9a-fA-F]{40}$/.test(address) && /^\d+$/.test(chain)
      ? `token/${chain}/${address}`
      : null;
  }

  return null;
}

export function getAdMedia(data: AdData | null): string | null {
  if (!data) return null;
  const fields = flatFields(data);
  for (const key of MEDIA_KEYS[data.type] ?? []) {
    const v = fields[key];
    if (typeof v === "string" && v) return v;
  }
  return null;
}

export function getAdImage(data: AdData | null): string | null {
  if (!data) return null;
  const fields = flatFields(data);
  for (const key of IMAGE_KEYS[data.type] ?? ANY_IMAGE_KEYS) {
    const v = fields[key];
    if (typeof v === "string" && v) return v;
  }
  return null;
}

/**
 * The ad's square mark — an app icon, a favicon, a token logo, an avatar.
 *
 * Falls through to the wide asset when there is no square one, so a caller
 * rendering a thumbnail always has something rather than a gap.
 */
export function getAdIcon(data: AdData | null): string | null {
  if (!data) return null;
  const fields = flatFields(data);
  for (const key of ICON_KEYS[data.type] ?? ANY_ICON_KEYS) {
    const v = fields[key];
    if (typeof v === "string" && v) return v;
  }
  return null;
}

/** Whoever published the ad's subject, where the ad type records one. */
export type AdCreator = {
  fid: number;
  username?: string | null;
  displayName?: string | null;
  pfpUrl?: string | null;
};

/**
 * The creator behind an ad, where its type carries one.
 *
 * Today only a mini app does — its manifest is signed by an fid, so the author
 * is part of what was verified rather than something looked up separately.
 */
export function getAdCreator(data: AdData | null): AdCreator | null {
  if (!data) return null;
  const creator = flatFields(data).creator as AdCreator | null | undefined;
  return creator && typeof creator.fid === "number" ? creator : null;
}

/** A post, in the fields a card needs to lay one out. */
export type AdPost = {
  text: string;
  /** The author's chosen name — what a reader recognises. */
  name: string | null;
  /** The handle, without the `@`. */
  handle: string | null;
  avatar: string | null;
  /** Epoch milliseconds, or null where the ad stored no readable date. */
  at: number | null;
  /** The post's own attachment. Never the avatar. */
  image: string | null;
};

/**
 * Ad types that are somebody saying something somewhere.
 *
 * A Farcaster cast and a post on X are the same object with different
 * plumbing, and their metadata is stored in the same shape deliberately, so
 * one card renders both and the network is a badge rather than a layout.
 */
const POST_TYPES = new Set(["cast", "tweet"]);

/**
 * The post behind an ad, where the ad is one.
 *
 * Returns null for every other type, so a layout can ask without knowing what
 * it is holding — which is the point, since a slot's creative changes under a
 * page that was written before it existed.
 *
 * `text` is allowed to be empty: a post that is only a picture is a real post,
 * and the card has an image to show. What makes this null is not being a post
 * at all.
 */
export function getAdPost(data: AdData | null): AdPost | null {
  if (!data || !POST_TYPES.has(data.type)) return null;

  const fields = flatFields(data);
  const str = (key: string) => {
    const v = fields[key];
    return typeof v === "string" && v ? v : null;
  };

  return {
    text: str("text") ?? "",
    name: str("displayName"),
    handle: str("username"),
    avatar: str("pfpUrl"),
    at: parseAdTimestamp(fields.timestamp),
    image: str("image"),
  };
}

export function getAdTitle(data: AdData | null): string | null {
  if (!data) return null;
  const fields = flatFields(data);
  for (const key of TITLE_KEYS) {
    const v = fields[key];
    if (typeof v === "string" && v) return v;
  }
  return null;
}

export function getAdDescription(data: AdData | null): string | null {
  if (!data) return null;
  const fields = flatFields(data);
  const title = getAdTitle(data);
  for (const key of DESC_KEYS) {
    const v = fields[key];
    if (typeof v === "string" && v && v !== title) return v;
  }
  return null;
}

export function getAdType(data: AdData | null): AdType | null {
  if (!data) return null;
  return data.type as AdType;
}
